/**
 * Custom hook for managing push notifications
 * Handles subscription, unsubscription, and permission management
 */
import { useState, useCallback, useEffect } from 'react';
import { safeFetch } from '../utils/safeFetch';

import { API_URL } from '../utils/api';

/**
 * Convert base64url string to Uint8Array for applicationServerKey
 * Follows Web Push VAPID spec for P-256 public key format
 */
function urlBase64ToUint8Array(base64String) {
  console.log('[Push] Input key:', base64String);
  console.log('[Push] Input key length:', base64String?.length);
  
  // Trim any whitespace that might have snuck in
  const cleanBase64 = (base64String || '').trim();
  console.log('[Push] Clean key length:', cleanBase64.length);
  
  // Add padding if needed (base64 requires length divisible by 4)
  const padding = '='.repeat((4 - cleanBase64.length % 4) % 4);
  console.log('[Push] Padding added:', padding.length, 'chars');
  
  // Convert from URL-safe base64 to standard base64
  const base64 = (cleanBase64 + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  console.log('[Push] Final base64 length:', base64.length);

  try {
    const rawData = window.atob(base64);
    console.log('[Push] Decoded length:', rawData.length);
    
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    // Validate P-256 public key format (65 bytes, starts with 0x04)
    if (outputArray.length !== 65) {
      console.error(`[Push] Invalid key length: ${outputArray.length} bytes (expected 65)`);
      console.error(`[Push] First few bytes: ${Array.from(outputArray.slice(0, 5)).map(b => '0x' + b.toString(16).padStart(2, '0')).join(', ')}`);
      throw new Error(`Invalid VAPID key length: ${outputArray.length}`);
    }
    if (outputArray[0] !== 4) {
      console.error(`[Push] Invalid key format: first byte is 0x${outputArray[0].toString(16)} (expected 0x04)`);
      throw new Error('Invalid VAPID key format');
    }

    console.log('[Push] VAPID key validated: 65 bytes, starts with 0x04');
    return outputArray;
  } catch (e) {
    console.error('[Push] Failed to decode VAPID key:', e);
    throw new Error('Failed to decode VAPID public key: ' + e.message);
  }
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', 'default'
}

/**
 * Custom hook for push notification management
 */
export function usePushNotifications(authToken) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState(getNotificationPermission());

  // Check subscription status on mount
  useEffect(() => {
    checkSubscriptionStatus();
  }, [authToken]);

  /**
   * Check if user is currently subscribed
   */
  const checkSubscriptionStatus = useCallback(async () => {
    if (!isPushSupported() || !authToken) {
      setIsLoading(false);
      return;
    }

    try {
      // Check browser subscription
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Verify with backend
        const response = await safeFetch(`${API_URL}/api/push/status`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (response.data && response.data.subscribed) {
          setIsSubscribed(true);
        } else {
          setIsSubscribed(false);
        }
      } else {
        setIsSubscribed(false);
      }
    } catch (err) {
      console.error('Error checking subscription status:', err);
      setIsSubscribed(false);
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setError('Notifications not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (err) {
      setError('Failed to request notification permission');
      return false;
    }
  }, []);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    console.log('[Push] Starting subscription process...');
    
    if (!isPushSupported()) {
      const errorMsg = 'Push notifications are not supported in this browser';
      console.error('[Push]', errorMsg);
      setError(errorMsg);
      return false;
    }

    if (!authToken) {
      setError('You must be logged in to enable notifications');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission first
      console.log('[Push] Current permission:', Notification.permission);
      if (Notification.permission === 'default') {
        const granted = await requestPermission();
        if (!granted) {
          setError('Notification permission denied');
          setIsLoading(false);
          return false;
        }
      } else if (Notification.permission === 'denied') {
        setError('Notification permission was previously denied. Please enable it in your browser settings.');
        setIsLoading(false);
        return false;
      }

      // Get VAPID public key from server
      console.log('[Push] Fetching VAPID key from server...');
      const keyResponse = await safeFetch(`${API_URL}/api/push/vapid-public-key`);
      if (!keyResponse.data || !keyResponse.data.publicKey) {
        throw new Error('Failed to get VAPID public key from server');
      }
      
      console.log('[Push] Received VAPID key, length:', keyResponse.data.publicKey.length);
      
      // Convert key to Uint8Array
      let applicationServerKey;
      try {
        applicationServerKey = urlBase64ToUint8Array(keyResponse.data.publicKey);
        console.log('[Push] Key converted successfully, byte length:', applicationServerKey.length);
      } catch (keyError) {
        console.error('[Push] Key conversion failed:', keyError);
        throw new Error('Invalid VAPID key format: ' + keyError.message);
      }

      // Wait for service worker to be ready
      console.log('[Push] Waiting for service worker...');
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready, subscribing to push manager...');

      // Subscribe to push service
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('[Push] Push subscription created successfully');
      } catch (pushError) {
        // Provide more specific error messages for common issues
        console.error('[Push] PushManager.subscribe failed:', pushError);
        
        if (pushError.name === 'NotAllowedError') {
          throw new Error('Notification permission was denied. Please allow notifications in your device settings.');
        } else if (pushError.name === 'InvalidStateError') {
          throw new Error('Push subscription is in an invalid state. Please try again.');
        } else if (pushError.message?.includes('applicationServerKey')) {
          throw new Error('Server configuration error. Please contact support.');
        } else {
          throw new Error('Failed to subscribe: ' + (pushError.message || 'Unknown error'));
        }
      }

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      console.log('[Push] Sending subscription to backend...');
      
      const response = await safeFetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys,
          expirationTime: subscriptionJson.expirationTime
        })
      });

      if (response.data && response.data.success) {
        setIsSubscribed(true);
        console.log('[Push] Successfully subscribed to push notifications');
        return true;
      } else {
        throw new Error(response.data?.message || 'Backend failed to save subscription');
      }
    } catch (err) {
      console.error('[Push] Subscription error:', err);
      setError(err.message || 'Failed to subscribe to notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [authToken, requestPermission]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    if (!isPushSupported() || !authToken) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Notify backend
        const subscriptionJson = subscription.toJSON();
        await safeFetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys
          })
        });

        // Unsubscribe from push service
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      console.log('Successfully unsubscribed from push notifications');
      return true;
    } catch (err) {
      console.error('Unsubscription error:', err);
      setError(err.message || 'Failed to unsubscribe');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  /**
   * Toggle subscription state
   */
  const toggleSubscription = useCallback(async () => {
    if (isSubscribed) {
      return unsubscribe();
    } else {
      return subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSubscribed,
    isLoading,
    error,
    permission,
    isSupported: isPushSupported(),
    subscribe,
    unsubscribe,
    toggleSubscription,
    requestPermission,
    checkSubscriptionStatus
  };
}

export default usePushNotifications;
